import { createSignalWithValue } from "@/client/utils/signal.js";

export interface AuthTokenState {
    accessToken: string | undefined;
    expiresAt: number;
}

export const authTokenSignal = createSignalWithValue<AuthTokenState>({
    accessToken: undefined,
    expiresAt: 0,
});

const pendingFirstToken: { resolve: (() => void) | undefined } = { resolve: undefined };
let firstTokenPromise: Promise<void> | undefined;

// Hosts without the auth bridge (older plugin builds) never send a token.
// After the first wait times out, stop stalling subsequent requests.
let didHostTimeOut = false;

let requestNewTokenFn: (() => void) | undefined;

// The website delivers its token itself, through Auth0, a moment after boot.
// Until it says so, a plain page is treated as having no token source at all.
let isTokenExpected = false;

/**
 * A token is on its way from this page's own login: wait for it the way an
 * embedded page waits for its host's, instead of sending the first requests
 * bare and taking a 401 for each.
 */
export const expectAuthToken = (): void => {
    isTokenExpected = true;
};

/** How close to expiry a token is refreshed ahead of a request rather than used. */
const kExpiryMargin_ms = 30_000;

/**
 * Whether the token in hand is expired or about to be. A request sent with it
 * would be refused, refreshed and sent again; refreshing first costs one
 * round-trip less and never shows a 401. An unknown expiry (0) is trusted.
 */
export const isAuthTokenStale = (now = Date.now()): boolean => {
    const { accessToken, expiresAt } = authTokenSignal.value;
    return accessToken !== undefined && expiresAt > 0 && expiresAt - now < kExpiryMargin_ms;
};

export const setAuthToken = (accessToken: string, expiresAt: number): void => {
    didHostTimeOut = false;
    authTokenSignal.notify({ accessToken, expiresAt });
    pendingFirstToken.resolve?.();
    pendingFirstToken.resolve = undefined;
};

export const clearAuthToken = (): void => {
    authTokenSignal.notify({ accessToken: undefined, expiresAt: 0 });
};

/**
 * Registered by the host-specific communication layer (plugin or web).
 * Calling `requestAuthToken()` triggers the host to send a fresh AuthToken
 * message. Returns a promise that resolves once a new token arrives or
 * times out.
 */
export const registerRequestAuthToken = (fn: () => void): void => {
    requestNewTokenFn = fn;
};

let inflightTokenRequest: Promise<void> | undefined;

export const requestAuthToken = (timeoutMs: number): Promise<void> => {
    if (requestNewTokenFn === undefined) {
        return Promise.resolve();
    }
    // Coalesce concurrent refreshes (parallel requests all hitting 401):
    // one request to the host, every caller resolves on the same token.
    if (inflightTokenRequest !== undefined) {
        return inflightTokenRequest;
    }
    firstTokenPromise = new Promise<void>((resolve) => {
        pendingFirstToken.resolve = resolve;
    });
    requestNewTokenFn();
    inflightTokenRequest = Promise.race([
        firstTokenPromise,
        new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]).then(() => {
        inflightTokenRequest = undefined;
    });
    return inflightTokenRequest;
};

/**
 * Resolves once an access token has been received from the host (or
 * immediately if one is already present). Times out so requests don't hang
 * forever if the host never sends a token.
 */
export const waitForAuthToken = (timeoutMs: number): Promise<void> => {
    if (authTokenSignal.value.accessToken !== undefined) {
        return Promise.resolve();
    }
    const isEmbedded = typeof window !== "undefined" && window.parent !== window;
    if (!isEmbedded && !isTokenExpected) {
        // Not embedded and no login of its own: there is nothing that could
        // deliver a token, so waiting would just stall every request.
        return Promise.resolve();
    }
    if (didHostTimeOut) {
        return Promise.resolve();
    }
    firstTokenPromise ??= new Promise<void>((resolve) => {
        pendingFirstToken.resolve = resolve;
    });
    return Promise.race([
        firstTokenPromise,
        new Promise<void>((resolve) =>
            setTimeout(() => {
                if (authTokenSignal.value.accessToken === undefined) {
                    didHostTimeOut = true;
                }
                resolve();
            }, timeoutMs),
        ),
    ]);
};
